import type { TFunction } from "i18next";
import { bookingsApi, BookingApiError, type PriceQuote } from "@/lib/bookings-api";
import { composeCheckoutTotal } from "@/lib/checkout-total";
import { markFreshBooking } from "@/lib/fresh-booking";
import { toRealError } from "@/lib/normalize-error";
import type { RentalType } from "@/components/checkout/CheckoutRentalTypeEditor";
import type { PaymentError, PaymentErrorKind } from "@/components/checkout/InlinePayment";
import { clearSecureCheckoutState } from "@/lib/checkout-state";
import { useEffect, useRef } from "react";
import { readPendingCheckout, savePendingCheckout } from "@/lib/pending-checkout";
import { OccasionSelectionRequiredError, isOccasionFieldApiError } from "@/lib/checkout-occasion";
import { CheckoutContractChangedError, type CheckoutCaptureMethod } from "@/lib/checkout-payment-contract";
import { CHECKOUT_STATUS_UNAVAILABLE_MESSAGE, isCheckoutStatusUnavailable } from "@workspace/booking-rules";

type PaymentTiming = "pay_now" | "pay_later";
type CancellationPolicy = "non-refundable" | "flexible";

function isAvailabilityConflict(err: unknown): err is BookingApiError {
  if (!(err instanceof BookingApiError)) return false;
  if (["dates_unavailable", "dates_conflict_hold", "dates_conflict_renter_own"].includes(String(err.code))) return true;
  return /dates?\s+conflict|some dates unavailable/i.test(String(err.message || err.body?.message || ""));
}

function timedConflictCopy(rentalType: RentalType, t: TFunction): string {
  return rentalType === "chauffeured" || rentalType === "event"
    ? t(
        "booking.timed_booking_conflict",
        "Another booking or checkout hold overlaps the selected time or the required one-hour preparation/turnaround buffer. Please choose a different time.",
      )
    : t("checkout.dates_conflict_generic", "Some dates are no longer available. Please choose different dates.");
}

/**
 * Sentinel thrown by prepareBooking when the booking finished without needing
 * an in-browser confirmation step. We navigate inside prepareBooking and the
 * calling Stripe submit handler should silently exit. The contract the Stripe
 * child relies on is the message string ("__no_payment_required__"), not the
 * class identity.
 */
export class BookingNoPaymentRequiredError extends Error {
  constructor() { super("__no_payment_required__"); }
}

export interface UseCheckoutPaymentIntentParams {
  token: string | undefined;
  carId: number;
  dateFrom: string;
  dateTo: string;
  rentalType: RentalType;
  hoursParam: number | undefined;
  selectedInsuranceId: number | null;
  selectedCancellationPolicy: CancellationPolicy;
  /** Task #8008 — live ref to the renter's cancellation-policy choice,
   *  maintained synchronously by CheckoutPage's H1 concurrency fix. When
   *  provided, prepareBooking reads the policy from here at CALL time rather
   *  than from the `selectedCancellationPolicy` prop captured at render time,
   *  so a rapid wallet tap after a policy switch (before React re-renders)
   *  can never submit the booking with the stale policy. Optional for
   *  backwards compat with test renders. */
  selectedCancellationPolicyRef?: { current: CancellationPolicy };
  appliedPromoCode: string;
  /** Task #8023 — live ref to the renter's applied promo code, maintained
   *  synchronously by CheckoutPage's H1 concurrency fix. When provided,
   *  prepareBooking reads the coupon code from here at CALL time rather than
   *  from the `appliedPromoCode` prop captured at render time, so a rapid
   *  wallet tap right after applying or removing a promo can never submit
   *  the booking with the stale (pre-apply or pre-remove) code.
   *  Optional for backwards compat with test renders. */
  appliedPromoCodeRef?: { current: string };
  /** Task #8023 — live ref to the renter's selected insurance package id,
   *  maintained synchronously by CheckoutPage's H1 concurrency fix. When
   *  provided, prepareBooking reads the insurance id from here at CALL time
   *  rather than from the `selectedInsuranceId` prop captured at render time,
   *  so a rapid wallet tap right after switching insurance can never submit
   *  the booking with the stale coverage selection.
   *  Optional for backwards compat with test renders. */
  selectedInsuranceIdRef?: { current: number | null };
  numPassengers: number | undefined;
  climateOffsetFlagOn: boolean;
  climateOffsetEnabled: boolean;
  deliveryDistanceParam: number | undefined;
  airportCodeParam: string | undefined;
  deliveryAddress: string | null;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  workTrip: boolean;
  businessName: string;
  vatNumber: string;
  paymentTiming: PaymentTiming;
  messageToOwner: string;
  pickupTimeParam: string | undefined;
  dropoffTimeParam: string | undefined;
  occasionParam: string | undefined;
  validateOccasionSelection?: () => boolean;
  onOccasionApiError?: () => void;
  // Task #4316: when present, the booking is created against a host "Build a
  // trip" custom quote — the server re-validates the token (status/expiry/
  // renter/terms), applies the override fees and confirms the trip instantly.
  quoteToken?: string | undefined;
  /** The custom-quote token must be resolved at the moment payment starts.
   * Wallet callbacks can outlive the render that initially mounted them. */
  quoteTokenRef?: { current: string | undefined };
  // Task #5598 — win-back conversion attribution. When the renter arrived on
  // checkout via a declined-booking win-back deep-link (?winback=<token>), the
  // token is forwarded on booking creation so the server flips the win-back
  // offer to `converted` and stops the follow-up cadence.
  winBackToken?: string | undefined;
  // Task #5598 ext — availability-probe instant-confirm consent token from a
  // win-back deep-link (?probe=<token>). When present the booking is routed to
  // the legacy POST /bookings path, which validates the consent and forces
  // instant confirmation; PFC's inline resolver does not consume it.
  probeToken?: string | undefined;
  quote: PriceQuote | null;
  draftStorageKey: string | null;
  secureCheckoutFlowId?: string;
  recoverySessionPublicId?: string;
  navigate: (to: string) => void;
  toast: (opts: { title: string; description?: string; variant?: "default" | "destructive" }) => void;
  t: TFunction;
  setOwnConflictTripIds: (v: number[] | null) => void;
  setPendingCancelledNotice: (v: string | null) => void;
  setOtherRenterHoldExpiresAt: (v: string | null) => void;
  setPhoneVerifiedClient: (v: boolean) => void;
  setPhoneUnverifiedFromServer: (v: boolean) => void;
  setPhoneVerificationError: (v: string | null) => void;
  setPendingPhoneScroll: (v: boolean) => void;
  setBookingPublicId: (v: string | null) => void;
  setPaymentError: (v: PaymentError | null) => void;
  setBookingStep: (v: "form" | "confirming") => void;
  // Task #3418 — Payment-first checkout flag. When true, routes through
  // POST /checkout-sessions-pfc (no trip row until Stripe confirms the card).
  pfcEnabled?: boolean;
  // Task #5038 — true while the feature-flags fetch is still in flight.
  // `pfcEnabled` defaults to false, so a prepareBooking that runs before flags
  // resolve (e.g. a wallet / Express Checkout confirm tapped during load) would
  // silently take the legacy create-trip-then-charge path and leave a visible
  // trip if the charge then fails. prepareBooking hard-aborts while this is true
  // so NO booking is created with an unresolved flag. The card Pay button and
  // the wallet UI are also gated on this; this is the authoritative backstop.
  pfcFlagLoading?: boolean;
  // Called during PFC "Finalizing booking…" interstitial to surface state.
  setFinalizingBooking?: (v: boolean) => void;
  checkoutJourneyId?: string;
  /** Required only for US self-drive checkout; omitted by legacy consumers. */
  usSelfDrivePrimaryDriverRequired?: boolean;
  primaryDriverChoiceRef?: { current: boolean | undefined };
  primaryDriverEmailRef?: { current: string };
  renterEmail?: string | null;
}

export interface UseCheckoutPaymentIntentResult {
  prepareBooking: (paymentMethodId?: string) => Promise<{ clientSecret: string; publicId: string; serverTotal?: number | null; captureMethod?: CheckoutCaptureMethod }>;
  handlePaymentSuccess: (publicId: string) => Promise<void>;
  handlePaymentError: (msg: string, kind?: PaymentErrorKind) => void;
  retryFinalization?: () => Promise<void>;
  /** Fast, AI-free keyword pre-check of the "message to host" note, run by the
   *  Pay button BEFORE any Stripe contact. Resolves true when the note is
   *  confidently bad (the toast is shown here); false otherwise. Fails open. */
  precheckNote: () => Promise<boolean>;
}

/**
 * Extracts the deferred-intent booking creation + payment result handling from
 * CheckoutPage. Behaviour is unchanged: prepareBooking creates the trip +
 * intent server-side right when the renter triggers a charge and returns the
 * clientSecret so the child can confirm inline.
 *
 * Task #1055 chose deferred-intent Stripe Elements over a literal eager
 * createBooking on payment-step mount: deferred mode lets PaymentElement +
 * ExpressCheckoutElement render immediately without a clientSecret, and
 * creating the trip on submit avoids orphan trips when the renter abandons
 * mid-checkout, fiddles with insurance / promo / plan inputs, or fails 3DS.
 *
 * Task #3418 adds the `pfcEnabled` flag-branch: when on, creates a
 * checkout_session (no trip) + Stripe PI, confirms inline, then polls for
 * the materialized trip via GET /checkout-sessions-pfc/:id/trip.
 */
export function useCheckoutPaymentIntent(
  params: UseCheckoutPaymentIntentParams,
): UseCheckoutPaymentIntentResult {
  const currentToken = useRef(params.token);
  currentToken.current = params.token;
  const {
    token, carId, dateFrom, dateTo, rentalType, hoursParam,
    selectedInsuranceId, selectedCancellationPolicy, selectedCancellationPolicyRef,
    appliedPromoCode, appliedPromoCodeRef, selectedInsuranceIdRef, numPassengers,
    climateOffsetFlagOn, climateOffsetEnabled,
    deliveryDistanceParam, airportCodeParam, deliveryAddress, deliveryLatitude, deliveryLongitude,
    workTrip, businessName, vatNumber, paymentTiming, messageToOwner,
    pickupTimeParam, dropoffTimeParam, occasionParam, validateOccasionSelection, onOccasionApiError,
    quoteToken, quoteTokenRef,
    winBackToken,
    probeToken,
    quote, draftStorageKey, secureCheckoutFlowId, navigate, toast, t,
    setOwnConflictTripIds, setPendingCancelledNotice, setOtherRenterHoldExpiresAt,
    setPhoneVerifiedClient, setPhoneUnverifiedFromServer, setPhoneVerificationError,
    setPendingPhoneScroll, setBookingPublicId, setPaymentError, setBookingStep,
    pfcEnabled = false,
    pfcFlagLoading = false,
    setFinalizingBooking,
    checkoutJourneyId = "",
    usSelfDrivePrimaryDriverRequired = false,
    primaryDriverChoiceRef,
    primaryDriverEmailRef,
    renterEmail,
  } = params;

  // Task #3396 — remember the public_id of the most recent trip we created
  // server-side, so handlePaymentError can fire-and-forget the abandon-payment
  // cleanup for it (release the renter's own pending trip + calendar holds the
  // moment a 3DS / card attempt fails, instead of leaving the orphan to block
  // their retry until the ~20-min hold-expiry cron sweeps it).
  // Task #3418: for PFC, this is the session public_id (no trip yet).
  const lastBookingPublicIdRef = useRef<string | null>(null);
  // Track whether the last prepareBooking was PFC so handlePaymentError knows
  // whether to call abandonPayment or not.
  const lastWasPfcRef = useRef(false);
  const pendingPfcRef = useRef(readPendingCheckout(secureCheckoutFlowId));
  const pendingFlowRef = useRef(secureCheckoutFlowId);
  if (pendingFlowRef.current !== secureCheckoutFlowId) {
    pendingFlowRef.current = secureCheckoutFlowId;
    pendingPfcRef.current = readPendingCheckout(secureCheckoutFlowId);
  }
  if (pendingPfcRef.current) lastWasPfcRef.current = true;
  useEffect(() => {
    if (pendingPfcRef.current) {
      setPaymentError({ message: CHECKOUT_STATUS_UNAVAILABLE_MESSAGE, kind: "status_unavailable" });
    }
  }, [secureCheckoutFlowId]);
  const clearCompletedDraft = () => {
    if (draftStorageKey) try { sessionStorage.removeItem(draftStorageKey); } catch { /* noop */ }
    if (secureCheckoutFlowId) clearSecureCheckoutState(secureCheckoutFlowId);
    savePendingCheckout(secureCheckoutFlowId, null);
    pendingPfcRef.current = null;
  };
  // A resumed PFC session may already be materialized. Keep that trip ID
  // explicit so handlePaymentSuccess navigates directly instead of treating it
  // as a checkout-session ID and polling/reconciling the wrong endpoint.
  const lastMaterializedPfcTripPublicIdRef = useRef<string | null>(null);
  // Task #4736 — track whether the last booking used a SAVED card. The
  // saved-card path fires the request fan-out synchronously server-side, so
  // only the NEW-card path needs the client-side confirm-request-hold ping
  // after the renter completes 3DS / authorization.
  const lastUsedSavedCardRef = useRef(false);

  // Task #8008 — resolve the cancellation policy at booking-creation time
  // from the live ref when available (see UseCheckoutPaymentIntentParams).
  const liveCancellationPolicy = (): CancellationPolicy =>
    selectedCancellationPolicyRef?.current ?? selectedCancellationPolicy;

  // Task #8023 — resolve the promo code and insurance id at booking-creation
  // time from the live refs when available, mirroring the pattern above.
  const livePromoCode = (): string =>
    appliedPromoCodeRef?.current ?? appliedPromoCode;

  const liveInsuranceId = (): number | null =>
    selectedInsuranceIdRef !== undefined
      ? selectedInsuranceIdRef.current
      : selectedInsuranceId;
  const liveQuoteToken = (): string | undefined =>
    quoteTokenRef !== undefined ? quoteTokenRef.current : quoteToken;

  const contractChangedMessage = () => t(
    "checkout.refresh_quote_capture_method",
    "This quote changed while you were checking out. Refresh the quote and try again so your payment form can be updated.",
  );
  const assertQuoteContext = () => {
    if (quoteTokenRef !== undefined && liveQuoteToken() !== quoteToken) {
      setBookingStep("form");
      throw new CheckoutContractChangedError(contractChangedMessage());
    }
  };

  const primaryDriverChoice = (): { renterIsDriver?: boolean | null; email: string } => {
    if (!usSelfDrivePrimaryDriverRequired) {
      return { renterIsDriver: rentalType === "driving" ? null : undefined, email: "" };
    }
    const choice = primaryDriverChoiceRef?.current;
    if (choice === undefined) {
      throw new Error(t("booking.primary_driver.checkout_required", "Choose whether you will be the primary driver."));
    }
    const email = primaryDriverEmailRef?.current.trim().toLowerCase() ?? "";
    if (!choice && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email === renterEmail?.trim().toLowerCase())) {
      throw new Error(email === renterEmail?.trim().toLowerCase()
        ? t("booking.primary_driver.checkout_distinct_email", "Enter an email address different from your renter account email.")
        : t("booking.primary_driver.checkout_valid_email", "Enter a valid primary-driver email address."));
    }
    return { renterIsDriver: choice, email };
  };

  const PRIMARY_DRIVER_REPAIR_PREFIX = "bac-primary-driver-repair:";
  const invitePrimaryDriverIfNeeded = async (tripPublicId: string): Promise<void> => {
    if (!token || !usSelfDrivePrimaryDriverRequired || primaryDriverChoiceRef?.current !== false) return;
    const email = primaryDriverEmailRef?.current.trim().toLowerCase() ?? "";
    const repairKey = `${PRIMARY_DRIVER_REPAIR_PREFIX}${tripPublicId}`;
    try {
      await bookingsApi.setPrimaryDriver(token, tripPublicId, { renterIsDriver: false, email });
      try { sessionStorage.removeItem(repairKey); } catch { /* storage is best-effort */ }
    } catch {
      // Booking creation/payment has already succeeded. Never retry the booking
      // mutation here: retain the booking id + selected email for confirmation-page repair.
      try {
        sessionStorage.setItem(repairKey, JSON.stringify({ email }));
      } catch { /* the existing selection card still allows manual repair */ }
    }
  };

  // Payment-first booking parameters shared across card/SetupIntent paths.
  const commonBookingParams = (paymentMethodId?: string) => ({
    recoverySessionPublicId: params.recoverySessionPublicId,
    primaryDriverEmail: primaryDriverChoice().renterIsDriver === false
      ? primaryDriverEmailRef?.current.trim().toLowerCase() : undefined,
    carId,
    dateFrom,
    dateTo,
    rentalType,
    hours: rentalType === "event" ? undefined : hoursParam,
    paymentMethodId: paymentMethodId || undefined,
    insurancePackageId:
      rentalType === "driving" ? (liveInsuranceId() || undefined) : undefined,
    cancellationPolicy: liveCancellationPolicy(),
    couponCode: livePromoCode() || undefined,
    quoteToken: liveQuoteToken(),
    probeToken: probeToken || undefined,
    pickupProof: quote?.pickupProof,
    expectedCaptureMethod: quote?.expectedCaptureMethod,
    numPassengers:
      numPassengers && Number.isFinite(numPassengers) ? numPassengers : undefined,
    climateOffset:
      climateOffsetFlagOn && climateOffsetEnabled ? true : undefined,
    deliveryDistance: deliveryDistanceParam,
    airportCode: airportCodeParam,
    pickupTime: pickupTimeParam,
    dropoffTime: dropoffTimeParam,
    occasion: occasionParam,
    winBackToken: winBackToken || undefined,
    // Task #6837 — always-visible clickwrap/MIT consent notice is rendered
    // above the Pay button, so the renter has accepted the Terms + MIT
    // authorization by the time they trigger a charge. Persist consent on the
    // checkout session so materialization retains the same evidence.
    checkout_terms_accepted: true,
    checkout_occasion_capability: "required-v1" as const,
    checkout_driver_choice_capability: "required-v1" as const,
    renterIsDriver: primaryDriverChoice().renterIsDriver,
  });

  const bookingPath = (publicId: string): string => `/bookings/${publicId}`;

  // Task #5797 — a request / no-payment booking (e.g. a Rest-of-World
  // self-drive request that charges nothing up front) creates its trip row
  // synchronously in POST /bookings, but a read-after-write lag can leave the
  // confirmation page's first GET /bookings/:publicId 404ing for a beat — the
  // renter sees a false "Booking not found" and re-submits, creating a
  // duplicate request. Poll the trip-detail endpoint until it resolves (up to
  // ~30s, the same bounded window as waitForPfcTrip) so we only navigate to a
  // page that will actually load. Returns false on an unresolved race, so the
  // caller can land the renter on /bookings instead of a specific
  // /bookings/:publicId that can 404.
  const waitForTripQueryable = async (tripPublicId: string): Promise<boolean> => {
    if (!token) return false;
    const MAX_ATTEMPTS = 20;
    const DELAY_MS = 1500;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        await bookingsApi.getTripDetail(token, tripPublicId);
        return true;
      } catch {
        // 404 (not queryable yet) or a transient network hiccup — keep polling
        // within the bounded window, then fall back to /bookings.
      }
      await new Promise<void>((res) => setTimeout(res, DELAY_MS));
    }
    return false;
  };

  // prepareBooking — called by the inline Pay button (or the
  // ExpressCheckoutElement onConfirm) right when the renter triggers a charge.
  // Creates the trip + intent server-side, returns the clientSecret so the
  // child can call stripe.confirmPayment / confirmCardPayment inline.
  const prepareBookingImpl = async (
    paymentMethodId?: string,
  ): Promise<{ clientSecret: string; publicId: string; serverTotal?: number | null; captureMethod?: CheckoutCaptureMethod }> => {
    if (!token || currentToken.current !== token) throw new Error("Not authenticated");
    // A retained wallet/card callback owns the quote and booking terms of its
    // render. Never pair those terms with a newly loaded or removed offer.
    assertQuoteContext();
    // Task #5038 — fail closed while the PFC feature flag is still resolving.
    // `pfcEnabled` defaults to false, so if a wallet / Express Checkout confirm
    // (or any caller) reaches prepareBooking before flags load, the legacy
    // create-trip-then-charge branch below would run and leave a visible trip
    // when the charge then fails. Abort BEFORE either branch so no booking is
    // ever created against an unresolved flag. The card Pay button and the
    // wallet UI are also gated on this, so in normal use this never fires.
    if (pfcFlagLoading) {
      setBookingStep("form");
      throw new Error("__pfc_flags_loading__");
    }
    if (validateOccasionSelection && !validateOccasionSelection()) {
      setBookingStep("form");
      throw new OccasionSelectionRequiredError();
    }
    // Validate the updated US self-drive driver decision at the mutation
    // boundary too, so wallet and saved-card paths cannot bypass the form gate.
    primaryDriverChoice();
    const usingSavedCard = !!paymentMethodId;
    lastMaterializedPfcTripPublicIdRef.current = null;
    // Task #4736 — remember the card path for handlePaymentSuccess (only the
    // new-card path needs the client confirm-request-hold ping).
    lastUsedSavedCardRef.current = usingSavedCard;
    void import("@/lib/analytics").then(({ track }) => {
      track("payment_info_entered", {
        car_id: carId,
        currency: quote?.currency || "USD",
        value: quote?.paymentDue,
        method: usingSavedCard ? "saved_card" : "new_card",
        checkout_journey_id: checkoutJourneyId,
      });
    });

    // ── Payment-first checkout branch ──────────────────────────────────────
    // Task #4948 — custom-quote acceptance (`quoteToken` set) now runs natively
    // through PFC: the session endpoint threads the token, re-validates the
    // quote's terms, applies the host's rental/delivery override, forces instant
    // booking, and consumes the quote (mark paid + flip the conversation card +
    // re-point the conversation) at materialize time. The earlier `&& !quoteToken`
    // carve-out that routed quotes to the legacy POST /bookings path is gone.
    // Every new booking uses payment-first, including availability offers.
    if (!pfcEnabled) {
      setBookingStep("form");
      throw new Error("Checkout is temporarily unavailable. Please try again later.");
    }
    if (pfcEnabled) {
      let sessionResult;
      let paymentReady: boolean | undefined;
      let preflightStarted = false;
      try {
        sessionResult = await bookingsApi.createCheckoutSession(token, {
          ...commonBookingParams(paymentMethodId),
          checkoutJourneyId: checkoutJourneyId || undefined,
          // Task #3418-p2: fields missing from commonBookingParams that the
          // legacy createBooking path sends — must be forwarded to PFC so
          // materialize_trip_from_session carries them to the new trip row.
          paymentTiming: paymentTiming === "pay_later" ? "pay_later" : undefined,
          deliveryAddress: deliveryAddress || undefined,
          pickupLatitude:
            deliveryLatitude != null &&
            deliveryLongitude != null &&
            !airportCodeParam
              ? deliveryLatitude
              : undefined,
          pickupLongitude:
            deliveryLatitude != null &&
            deliveryLongitude != null &&
            !airportCodeParam
              ? deliveryLongitude
              : undefined,
          workTrip: workTrip || undefined,
          businessName:
            workTrip && businessName ? businessName : undefined,
          vatNumber: workTrip && vatNumber ? vatNumber : undefined,
          messageToOwner: messageToOwner || undefined,
          // Task #4948 — forward the accepted host quote token so PFC applies
          // the override + forces instant + consumes the quote.
          quoteToken: liveQuoteToken(),
        });
        if (sessionResult.requiresPayment && sessionResult.state !== "materialized") {
          lastBookingPublicIdRef.current = sessionResult.sessionPublicId;
          lastWasPfcRef.current = true;
          setBookingPublicId(sessionResult.sessionPublicId);
          preflightStarted = true;
          const readiness = await bookingsApi.preflightCheckoutSession(token, sessionResult.sessionPublicId);
          if (readiness.status !== "materialized" && typeof readiness.paymentReady !== "boolean") {
            throw new Error(t("checkout.session_failed_desc"));
          }
          paymentReady = readiness.status === "materialized" ? false : readiness.paymentReady;
        }
      } catch (err) {
        if (isOccasionFieldApiError(err)) {
          onOccasionApiError?.();
          setPaymentError(null);
          setBookingStep("form");
          throw new OccasionSelectionRequiredError();
        }
        // A stale quote must never be retried through legacy or silently
        // converted to automatic capture. The mounted Elements and quote need
        // to be refreshed together before another payment attempt.
        if (
          err instanceof BookingApiError &&
          (err.code === "capture_method_mismatch" ||
            err.body?.error === "capture_method_mismatch")
        ) {
          setPaymentError(null);
          setBookingStep("form");
          throw new CheckoutContractChangedError(contractChangedMessage());
        }
        // Task #3415 — any failed attempt must drop the modal back to the
        // editable form. The phone_unverified / message_to_owner_blocked
        // branches below abort WITHOUT calling handlePaymentError (to avoid a
        // duplicate inline banner), so the "Confirming your booking" overlay
        // would otherwise stay up forever and the renter could never edit the
        // note and retry. Idempotent with the reset handlePaymentError does on
        // the Stripe-confirm error path.
        setBookingStep("form");
        // Task #7146 — the "conflict" is the renter's OWN already-confirmed
        // booking for these dates (an earlier confirm succeeded server-side
        // while the client hiccuped and retried). This is a SUCCESS: land on
        // the booking's confirmation page, never a "Payment Failed" banner.
        if (err instanceof BookingApiError && err.code === "dates_conflict_own_booked") {
          setOwnConflictTripIds(null);
          setPendingCancelledNotice(null);
          setOtherRenterHoldExpiresAt(null);
          const tripPublicId =
            typeof err.body?.tripPublicId === "string" ? err.body.tripPublicId : null;
          toast({
            title: t("checkout.already_booked_title", "You're already booked"),
            description: t(
              "checkout.already_booked_desc",
              "Your earlier payment went through and this booking is confirmed — you have not been charged again.",
            ),
          });
          if (tripPublicId) {
            markFreshBooking(tripPublicId);
            navigate(bookingPath(tripPublicId));
          } else {
            navigate("/bookings");
          }
          throw err;
        }
        // Surface the same structured errors the legacy path handles
        if (err instanceof BookingApiError && err.code === "dates_unavailable") {
          setOwnConflictTripIds(null);
          setPendingCancelledNotice(null);
          setOtherRenterHoldExpiresAt(null);
          setPaymentError(null);
          toast({
            title: t("checkout.availability_lost_title", "Availability changed"),
            description: timedConflictCopy(rentalType, t),
            variant: "destructive",
          });
          throw err;
        }
        if (err instanceof BookingApiError && err.code === "dates_conflict_hold") {
          const expiresAt =
            err.body && typeof err.body.holdExpiresAt === "string"
              ? (err.body.holdExpiresAt as string)
              : null;
          setOtherRenterHoldExpiresAt(expiresAt);
          setOwnConflictTripIds(null);
          setPendingCancelledNotice(null);
          throw err;
        }
        // Availability can change between the widget probe and payment.
        // Stop before returning a secret to Stripe and use one calm,
        // rental-type-aware explanation rather than the generic failure toast.
        if (isAvailabilityConflict(err) && err.code !== "dates_conflict_hold" && err.code !== "dates_conflict_renter_own") {
          setPaymentError(null);
          toast({
            title: t("checkout.availability_lost_title", "Availability changed"),
            description: timedConflictCopy(rentalType, t),
            variant: "destructive",
          });
          throw err;
        }
        // Task #5456 — the renter's OWN pending request(s) block the dates and
        // the server couldn't auto-recover (ambiguous: multiple own orphans).
        // Surface the same "Cancel my pending request" banner the legacy path
        // shows instead of the generic toast.
        if (err instanceof BookingApiError && err.code === "dates_conflict_renter_own") {
          const ids = Array.isArray(err.body?.conflictingTrips)
            ? (err.body.conflictingTrips as Array<{ id: number | string }>)
                .map((tr) => Number(tr.id))
                .filter((n) => Number.isFinite(n))
            : [];
          setOwnConflictTripIds(ids.length > 0 ? ids : null);
          setPendingCancelledNotice(null);
          setOtherRenterHoldExpiresAt(null);
          throw err;
        }
        if (err instanceof BookingApiError && err.code === "phone_unverified") {
          setPhoneVerifiedClient(false);
          setPhoneUnverifiedFromServer(true);
          const msg =
            (typeof err.body?.message === "string" && err.body.message) ||
            err.message ||
            t(
              "checkout.phone_verification_required_error",
              "Please verify your phone number before booking.",
            );
          setPhoneVerificationError(msg);
          toast({
            title: t("checkout.phone_verification_heading", "Verify your phone number"),
            description: msg,
            variant: "destructive",
          });
          setPendingPhoneScroll(true);
          throw err;
        }
        if (
          err instanceof BookingApiError &&
          err.code === "message_to_owner_blocked"
        ) {
          // The renter's "message to host" note tripped the shared
          // moderation pipeline (contact details, off-platform payment,
          // external link, abuse or scam). Surface the server's
          // plain-language reason so the renter edits the note — same as
          // the legacy POST /bookings path. The throw below stops the flow
          // before any Stripe charge (PFC moderates before creating the hold).
          const msg =
            (typeof err.body?.message === "string" && err.body.message) ||
            err.message ||
            t("checkout.could_not_create_booking");
          // Task #4687 — clear any stale "Payment Failed" banner from a prior
          // attempt. The block aborts BEFORE any Stripe charge, so leaving a
          // red payment-failed banner up while we ask the renter to edit their
          // note is misleading. The toast below is the actionable surface.
          setPaymentError(null);
          toast({
            title: t(
              "checkout.message_to_owner_blocked_heading",
              "Please edit your message to the host",
            ),
            description: msg,
            variant: "destructive",
          });
          throw err;
        }
        if (
          err instanceof BookingApiError &&
          (err.code === "checkout_resume_pending" ||
            err.code === "checkout_resume_unavailable")
        ) {
          const pendingSessionPublicId =
            typeof err.body?.sessionPublicId === "string"
              ? err.body.sessionPublicId
              : null;
          if (pendingSessionPublicId) {
            lastBookingPublicIdRef.current = pendingSessionPublicId;
            lastWasPfcRef.current = true;
            setBookingPublicId(pendingSessionPublicId);
          }
          toast({
            title: t(
              "checkout.finalizing_timeout_title",
              "Booking still finalizing",
            ),
            description:
              err.code === "checkout_resume_pending"
                ? t(
                    "checkout.payment_status_unknown_desc",
                    CHECKOUT_STATUS_UNAVAILABLE_MESSAGE,
                  )
                : t(
                    "checkout.payment_status_unknown_desc",
                    "We couldn't safely check your earlier payment attempt. Please wait a moment and don't submit again yet.",
                  ),
          });
          throw err;
        }
        // A transport error may follow a server-side commit or a resumed
        // payment. Unknown outcomes must not be described as an unpaid failure.
        if (isCheckoutStatusUnavailable(err)) {
          // A timed-out POST may have completed server-side. Never present its
          // outcome as a decline or ask the payment-error handler to release it.
          setPaymentError({ message: CHECKOUT_STATUS_UNAVAILABLE_MESSAGE, kind: "status_unavailable" });
          throw new Error(CHECKOUT_STATUS_UNAVAILABLE_MESSAGE);
        }
        toast({
          title: t("checkout.booking_failed"),
          description: t(
            preflightStarted ? "checkout.session_failed_desc" : "checkout.pre_payment_failed_desc",
          ),
          variant: "destructive",
        });
        throw err;
      }

      // Store session publicId so handlePaymentError can clean up the holds
      lastBookingPublicIdRef.current = sessionResult.sessionPublicId;
      lastWasPfcRef.current = true;
      setBookingPublicId(sessionResult.sessionPublicId);

      // Task #5757 — the server transparently cleared the renter's OWN stale
      // checkout hold / pending-trip orphan and retried, so the dates that
      // moments ago looked "unavailable" are now free. Tell the renter WHY
      // instead of letting it happen silently — a positive, reassuring notice.
      if (sessionResult.recoveredOwnConflict) {
        setOwnConflictTripIds(null);
        setOtherRenterHoldExpiresAt(null);
        toast({
          title: t(
            "checkout.own_conflict_recovered_heading",
            "We cleared your earlier attempt",
          ),
          description: t(
            "checkout.own_conflict_recovered_desc",
            "Your previous unfinished booking for these dates was still holding them. We've released it so you can continue — you have not been charged twice.",
          ),
        });
      }

      void import("@/lib/analytics").then(({ track }) => {
        // Task #3418: reuse existing booking_requested event shape for PFC sessions.
        // Task #7874: now a typed event; maps to the Google Ads booking-request
        // conversion (the PFC path's booking-request submission step).
        track("booking_requested", {
          checkout_journey_id: checkoutJourneyId,
          car_id: carId,
          session_id: sessionResult.sessionPublicId,
          booking_path: "payment_first",
          currency: quote?.currency || "USD",
          value: quote?.paymentDue,
        });
      });

      // H3 — derive serverTotal from the client-side quote for the PFC path.
      // The session endpoint validates booking params at the same price as the
      // quote but does not return a re-priced quote in its response. Using the
      // client quote as the authority gives the H3 mismatch gate the same data
      // it uses on the legacy path: any drift between `displayedTotal` and the
      // quote the renter saw is caught before Stripe confirm runs.
      //
      // Task #4263 — compose serverTotal with the SAME helper that builds the
      // displayed `totalDue` in CheckoutPage so a climate offset / US sales-tax
      // add-on layered on top can never desync the two and trip a false drift
      // prompt. The partial-pay-aware base selection lives inside the composer.
      const pfcServerTotal = quote
        ? composeCheckoutTotal(quote, { climateOffsetFlagOn, climateOffsetEnabled })
        : null;
      if (sessionResult.state === "materialized") {
        if (!sessionResult.tripPublicId) {
          throw new Error("Materialized checkout session is missing its trip");
        }
        lastMaterializedPfcTripPublicIdRef.current = sessionResult.tripPublicId;
        return {
          clientSecret: "__no_payment_required__",
          publicId: sessionResult.tripPublicId,
          serverTotal: pfcServerTotal,
        };
      }
      if (paymentReady !== false) assertQuoteContext();
      return {
        clientSecret: paymentReady === false
          ? "__no_payment_required__"
          : sessionResult.clientSecret,
        publicId: sessionResult.sessionPublicId,
        serverTotal: pfcServerTotal,
        captureMethod: sessionResult.captureMethod,
      };
    }

    throw new Error("Checkout is temporarily unavailable. Please try again later.");
  };

  // Task #5995 — normalize any non-Error thrown/rejected value on the booking
  // path into a real Error (preserving message/name/stack) so, should it escape
  // to Sentry as an unhandledrejection, it reports readable content instead of
  // the opaque "captured as promise rejection with keys: …" placeholder. Real
  // Errors (BookingApiError, the no-payment sentinel, …) pass through unchanged,
  // so the existing inline-toast + re-throw handling stays intact.
  const prepareBooking = async (
    paymentMethodId?: string,
  ): Promise<{ clientSecret: string; publicId: string; serverTotal?: number | null; captureMethod?: CheckoutCaptureMethod }> => {
    try {
      if (pendingPfcRef.current) {
        throw Object.assign(new Error(CHECKOUT_STATUS_UNAVAILABLE_MESSAGE), {
          status: 503, code: "checkout_session_status_unavailable",
        });
      }
      return await prepareBookingImpl(paymentMethodId);
    } catch (err) {
      throw toRealError(err);
    }
  };

  // Task #3418 / #5669 — After PFC payment confirmation, poll until the webhook
  // has materialised the trip. Waits up to ~30s with 1.5s intervals.
  //
  // Returns a DISCRIMINATED result so the caller can tell the three outcomes
  // apart — a bare `null` conflated "session failed" with "still finalizing",
  // which is exactly what let the UI claim "payment accepted" when in fact
  // nothing had materialized. `materialized` is the ONLY status that proves a
  // real booking exists.
  const waitForPfcTrip = async (
    sessionPublicId: string,
  ): Promise<
    | { status: "materialized"; tripPublicId: string }
    | { status: "failed" }
    | { status: "timeout" }
  > => {
    if (!token) return { status: "timeout" };
    const MAX_ATTEMPTS = 20;
    const DELAY_MS = 1500;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        const poll = await bookingsApi.pollCheckoutTrip(token, sessionPublicId);
        if (poll.status === "materialized" && poll.tripPublicId) {
          return { status: "materialized", tripPublicId: poll.tripPublicId };
        }
        if (poll.status === "failed" || poll.status === "expired") {
          return { status: "failed" };
        }
      } catch {
        // Network hiccup — keep polling
      }
      if (i === 0) {
        // Do not depend on a webhook reaching this browser's DEV/origin server.
        // This reconciles the SAME owned provider object; it never starts payment.
        try {
          const reconciled = await bookingsApi.reconcileCheckoutSession(token, sessionPublicId);
          if (reconciled.status === "materialized" && reconciled.tripPublicId) {
            return { status: "materialized", tripPublicId: reconciled.tripPublicId };
          }
          if (reconciled.status === "failed") return { status: "failed" };
        } catch { /* A temporary failure still has the bounded poll/recheck path. */ }
      }
      await new Promise<void>((res) => setTimeout(res, DELAY_MS));
    }
    return { status: "timeout" };
  };

  const handlePaymentSuccess = async (publicId: string): Promise<void> => {
    // Non-PFC near-pickup instant deposits need the rental charge settled
    // while the renter is still on checkout. For other bookings this is a
    // cheap no-op; do not present an unpaid trip as confirmed on an error.
    if (token && !lastWasPfcRef.current) {
      try {
        await bookingsApi.finaliseNearPickup(token, publicId);
      } catch {
        toast({
          title: t("checkout.near_pickup_payment_title", "Rental payment needs attention"),
          description: t("checkout.near_pickup_payment_pending", "Your rental payment could not be completed before pickup. Please check Trips and contact support before starting the trip."),
          variant: "destructive",
        });
        setPaymentError({
          message: t("checkout.near_pickup_payment_pending", "Your rental payment could not be completed before pickup. Please check Trips and contact support before starting the trip."),
          kind: "charge_failed",
        });
        navigate("/trips");
        return;
      }
    }
    if (lastMaterializedPfcTripPublicIdRef.current === publicId) {
      clearCompletedDraft();
      lastMaterializedPfcTripPublicIdRef.current = null;
      setPaymentError(null);
      if (token && !lastUsedSavedCardRef.current) {
        void bookingsApi.confirmRequestHold(token, publicId).catch(() => { /* noop — idempotent backstop */ });
      }
      markFreshBooking(publicId);
      await invitePrimaryDriverIfNeeded(publicId);
      navigate(bookingPath(publicId));
      return;
    }

    // Task #3418 / #5669 — PFC path: publicId here is the sessionPublicId.
    // Poll for the materialized trip, then navigate ONLY to a trip we have
    // proven exists. Never claim "payment accepted" or navigate to a
    // /bookings/:publicId URL before the trip is confirmed to exist — that
    // was the 404 + false-success bug (a failed-before-payment session used
    // to route the renter to a confirmation page they'd never see).
    if (lastWasPfcRef.current) {
      // Completion follows the prepared payment, not a flag reloading after
      // guest authentication. Keep the handoff until a real trip is proven.
      pendingPfcRef.current = publicId;
      savePendingCheckout(secureCheckoutFlowId, publicId);
      setFinalizingBooking?.(true);
      setPaymentError(null);
      let result = await waitForPfcTrip(publicId);

      // Timed out with no trip yet, but a pre-auth may well have landed. Rather
      // than strand the renter until the 15-min reconcile cron, ask the server
      // to run the same idempotent materializer the webhook uses, right now,
      // then re-check once. Reduces dependence on a single webhook (Task #5669).
      if (result.status === "timeout" && token) {
        try {
          const rec = await bookingsApi.reconcileCheckoutSession(token, publicId);
          if (rec.status === "materialized" && rec.tripPublicId) {
            result = { status: "materialized", tripPublicId: rec.tripPublicId };
          } else if (rec.status === "failed") {
            result = { status: "failed" };
          }
        } catch {
          // Best-effort — fall through to the honest timeout messaging below.
        }
      }
      setFinalizingBooking?.(false);

      if (result.status === "materialized") {
        clearCompletedDraft();
        // Task #4736 — new-card request: trigger the renter↔host fan-out now
        // rather than waiting on the Stripe webhook. Self-gating + best-effort.
        if (token && !lastUsedSavedCardRef.current) {
          void bookingsApi.confirmRequestHold(token, result.tripPublicId).catch(() => { /* noop — webhook + cron backstop */ });
        }
        // Task #5797 — stamp the just-materialized trip so the confirmation
        // page can ride out any residual read-after-write lag on its first load.
        markFreshBooking(result.tripPublicId);
        await invitePrimaryDriverIfNeeded(result.tripPublicId);
        navigate(bookingPath(result.tripPublicId));
        return;
      }

      if (result.status === "failed") {
        pendingPfcRef.current = null;
        savePendingCheckout(secureCheckoutFlowId, null);
        // A failed booking can have a released authorization still visible at
        // the bank. Do not conflate "no booking" with "no pending card hold".
        toast({
          title: t("checkout.session_failed_title", "We couldn't complete your booking"),
          description: t(
            "checkout.session_failed_desc",
            "We couldn't complete your booking. A pending card hold may still appear in your bank app. Check Trips before trying again.",
          ),
          variant: "destructive",
        });
        handlePaymentError(
          t(
            "checkout.session_failed_desc",
            "We couldn't complete your booking. A pending card hold may still appear in your bank app. Check Trips before trying again.",
          ),
          "charge_failed",
        );
        return;
      }

      // Keep the selections and a status-only retry, never a fresh payment.
      toast({
        title: t("checkout.finalizing_timeout_title", "Booking still finalizing"),
        description: t(
          "checkout.payment_status_unknown_desc",
          CHECKOUT_STATUS_UNAVAILABLE_MESSAGE,
        ),
      });
      handlePaymentError(CHECKOUT_STATUS_UNAVAILABLE_MESSAGE, "status_unavailable");
      return;
    }

    // Per user request: skip the in-modal "Booking confirmed" interstitial
    // and go straight to /bookings/:publicId, which already renders the
    // "Waiting on host" state for request bookings (and the live booking
    // detail for instant). Removing the dwell + button reduces taps and
    // matches the renter's mental model — Pay → see my booking.
    clearCompletedDraft();
    setPaymentError(null);
    // Task #4736 — new-card request: trigger the renter↔host fan-out now
    // rather than waiting on the Stripe webhook. Self-gating + best-effort.
    if (token && !lastUsedSavedCardRef.current) {
      void bookingsApi.confirmRequestHold(token, publicId).catch(() => { /* noop — webhook + cron backstop */ });
    }
    // Task #5797 — stamp the just-created trip so the confirmation page can ride
    // out any residual read-after-write lag on its first load.
    markFreshBooking(publicId);
    await invitePrimaryDriverIfNeeded(publicId);
    navigate(bookingPath(publicId));
  };

  const handlePaymentError = (msg: string, kind: PaymentErrorKind = "charge_failed") => {
    // Task #1211 — pull the modal back to the form step and surface the
    // failure inside the modal (banner near the payment section) instead
    // of a transient toast that disappears before the renter can read it.
    // The Retry CTA inside the banner just clears the banner; the Pay
    // button below stays mounted with the same Stripe Elements state, so
    // re-clicking Pay submits with whatever new card / details the
    // renter entered.
    setBookingStep("form");
    if (msg === "__no_payment_required__" || msg === "__occasion_selection_required__") return;
    if (kind === "status_unavailable" || msg === CHECKOUT_STATUS_UNAVAILABLE_MESSAGE) {
      // No abandon/release call: a transport failure is not a failed payment.
      setPaymentError({ message: CHECKOUT_STATUS_UNAVAILABLE_MESSAGE, kind: "status_unavailable" });
      return;
    }
    if (kind === "card_validation" || kind === "location_verification") {
      setPaymentError({ message: msg, kind });
      return;
    }
    // Task #3396 — the inline 3DS / card confirm just failed. The trip +
    // per-day calendar holds were created server-side before this attempt, so
    // release them now (fire-and-forget) — otherwise the renter's own orphan
    // blocks their immediate retry with "dates are booked". Best-effort: a
    // failure here is harmless (the hold-expiry cron is the backstop) so we
    // never surface it over the actual payment error.
    // Payment-first checkout reports the failure to a guarded endpoint. The
    // server independently re-checks Stripe but never cancels a persisted
    // object inline: confirmation can race any status read. Payment progress is
    // materialized; uncertain states retain their hold for reconcile/expiry.
    // This remains fire-and-forget: the actionable card/wallet error is the
    // renter-facing outcome.
    if (token && lastBookingPublicIdRef.current && !lastWasPfcRef.current) {
      const abandonId = lastBookingPublicIdRef.current;
      lastBookingPublicIdRef.current = null;
      void bookingsApi.abandonPayment(token, abandonId).catch(() => { /* noop — cron backstop */ });
    }
    if (token && lastBookingPublicIdRef.current && lastWasPfcRef.current) {
      const sessionPublicId = lastBookingPublicIdRef.current;
      void bookingsApi.releaseFailedCheckoutSession(token, sessionPublicId).catch(() => {
        // The expiry sweep remains the closed-tab/network-loss backstop.
      });
    }

    // Task #4687 — carry the classification so the page banner can choose calm
    // ("check your card details") vs alarming ("Payment Failed") framing. A
    // card_validation error never released holds above because it's a pre-charge
    // field problem, but the abandon path is a harmless no-op when there is no
    // outstanding hold, so the shared cleanup above is safe for both kinds.
    setPaymentError({ message: msg, kind });
    // Scroll the banner into view so it isn't hidden under the sticky header.
    requestAnimationFrame(() => {
      try {
        document.querySelector('[data-testid="booking-modal-payment-error"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch { /* noop */ }
    });
  };

  // Fast, AI-free keyword pre-check of the "message to host" note, run by the
  // checkout Pay button BEFORE any Stripe contact (the new-card path calls
  // `elements.submit()` — a no-charge Stripe card-collection call — so without
  // this an obviously-bad note hit Stripe on every retry). Returns true when
  // the note is confidently bad (and shows the same toast as the server's
  // booking-submit block); false otherwise. Fails OPEN — a flaky pre-check
  // never blocks a booking, because the server's full AI gate at submit is the
  // authoritative backstop.
  const precheckNote = async (): Promise<boolean> => {
    const note = messageToOwner?.trim();
    if (!note || !token) return false;
    try {
      const res = await bookingsApi.precheckMessageToOwner(token, note);
      if (res.blocked) {
        // Task #4687 — clear any stale "Payment Failed" banner. The pre-check
        // aborts before any Stripe contact, so a leftover red payment-failed
        // banner is misleading while we ask the renter to edit their note.
        setPaymentError(null);
        toast({
          title: t(
            "checkout.message_to_owner_blocked_heading",
            "Please edit your message to the host",
          ),
          description:
            (typeof res.message === "string" && res.message) ||
            t("checkout.could_not_create_booking"),
          variant: "destructive",
        });
        // Task #3415 — the Pay button flipped the modal to "confirming" on
        // click; drop it back so the renter can edit the note and retry.
        setBookingStep("form");
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  return {
    prepareBooking, handlePaymentSuccess, handlePaymentError, precheckNote,
    retryFinalization: pendingPfcRef.current
      ? async () => {
        const pending = pendingPfcRef.current;
        if (pending) await handlePaymentSuccess(pending);
      }
      : undefined,
  };
}
